import { StateCreator } from 'zustand';
import { AppState } from '../types';
import { generateSimpleId, forceNumber } from '../utils';
import {
    PEProfitPrognose,
    PlanungsSzenario,
    PlanungsBasisDatenTyp,
    PEPnlBlock,
    Dienstleistungserweiterung,
    Investition,
    PersonalAnpassung,
    GemeinkostenDetailAnpassung,
    Unterschrift,
    PE_PROFIT_NEUER_MITARBEITER_ID,
    PE_PROFIT_DEFAULT_UMSATZ_FAKTOR_NEU,
    PE_PROFIT_DEFAULT_UMSATZ_FAKTOR_BISHER,
    PE_PROFIT_DEFAULT_UMSATZ_AUFFANG_PROZENT,
    createDefaultPEProfitPrognose,
} from '../../types/peProfit';
import { calculateAllMetrics } from '../selectors/calculations';
import { calculatePeProfitMetrics, resolveCalcForPlanungsBasis } from '../../hooks/usePeProfitCalculations';
import type { PeProfitWorkspace } from '../peProfitWorkspace';

// +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
// TYPE DEFINITIONS
// +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++

export type { PeProfitWorkspace };

export interface PeProfitSliceType {
    peProfitPrognose: PEProfitPrognose;
    /** Inkrement bei jeder PE-Profit-Mutation — erzwingt UI-Re-Renders */
    peProfitDataVersion: number;
    planungsArchiv: PlanungsSzenario[];
    activeSzenarioId: string | null;
    /** Datenbasis ohne aktives Planungs-Szenario (z. B. PE-Profit-Seite) */
    peProfitBasisDatenTyp: PlanungsBasisDatenTyp;

    /** Isolierter Workspace: Planungsanalyse (Kostenstruktur-Modal) vs PE-Profit-Page */
    peProfitWorkspace: PeProfitWorkspace;
    planungsAnalysePrognose: PEProfitPrognose;
    planungsAnalyseArchiv: PlanungsSzenario[];
    planungsAnalyseActiveSzenarioId: string | null;
    planungsAnalyseBasisDatenTyp: PlanungsBasisDatenTyp;

    setPeProfitWorkspace: (workspace: PeProfitWorkspace) => void;
    createPlanungsSzenario: (titel: string, basisDatenTyp: PlanungsBasisDatenTyp) => void;
    loadPlanungsSzenario: (id: string) => void;
    setActiveSzenarioId: (id: string | null) => void;
    updateActiveSzenarioBasis: (basis: PlanungsBasisDatenTyp) => void;
    setPeProfitBasisDatenTyp: (basis: PlanungsBasisDatenTyp) => void;
    renamePlanungsSzenario: (id: string, titel: string) => void;
    setActiveSzenarioBankerPitch: (text: string) => void;
    clearActiveSzenarioBankerPitch: () => void;
    deletePlanungsSzenario: (id: string) => void;

    setPreisAnpassung: (prozent: number) => void;
    /** null = automatischer Schwund (¼ der Preiserhöhung) */
    setKundenVerlustProzent: (prozent: number | null) => void;
    setMaterialKostenAnpassung: (prozent: number) => void;
    setMaterialZielMonat: (monat: number | null) => void;
    setMaterialNotiz: (notiz: string) => void;
    /** null = IST-Unternehmerlohn aus Unternehmerwerte */
    setUnternehmerLohnZielMonat: (monat: number | null) => void;
    setUnternehmerUmsatzDeltaMonat: (deltaMonat: number) => void;
    setUnternehmerUmsatzAufGesamtAnrechnen: (aktiv: boolean) => void;
    setUnternehmerUmsatzAuffangProzent: (prozent: number) => void;
    setAnsichtsModus: (modus: 'monat' | 'jahr') => void;
    setSzenarioName: (name: string) => void;

    addNeueDienstleistung: (defaults?: Partial<Omit<Dienstleistungserweiterung, 'id'>>) => void;
    updateNeueDienstleistung: (id: string, updates: Partial<Omit<Dienstleistungserweiterung, 'id'>>) => void;
    removeNeueDienstleistung: (id: string) => void;

    addGemeinkostenDetailAnpassung: (
        defaults: Pick<GemeinkostenDetailAnpassung, 'bwaKey' | 'label' | 'istWert' | 'neuerZielwert'> &
            Partial<Pick<GemeinkostenDetailAnpassung, 'notiz'>>
    ) => void;
    /** Live-Update: vorhandenen BWA-Posten aktualisieren oder bei Abweichung neu anlegen; bei IST=Ziel entfernen */
    upsertGemeinkostenDetailAnpassung: (
        defaults: Pick<GemeinkostenDetailAnpassung, 'bwaKey' | 'label' | 'istWert' | 'neuerZielwert'> &
            Partial<Pick<GemeinkostenDetailAnpassung, 'notiz'>>
    ) => void;
    updateGemeinkostenDetailAnpassung: (
        id: string,
        updates: Partial<Omit<GemeinkostenDetailAnpassung, 'id' | 'bwaKey'>>
    ) => void;
    removeGemeinkostenDetailAnpassung: (id: string) => void;

    addNeueInvestition: (defaults?: Partial<Omit<Investition, 'id'>>) => void;
    updateNeueInvestition: (id: string, updates: Partial<Omit<Investition, 'id'>>) => void;
    removeNeueInvestition: (id: string) => void;

    addPersonalAnpassung: (defaults?: Partial<Omit<PersonalAnpassung, 'id'>>) => void;
    /** Live-Update für bestehende Mitarbeiter; bei IST=Ziel entfernen */
    upsertPersonalAnpassung: (
        defaults: Pick<PersonalAnpassung, 'mitarbeiterId' | 'mitarbeiterName' | 'istWert' | 'neuerZielwert'> &
            Partial<
                Pick<
                    PersonalAnpassung,
                    'entlassen' | 'umsatzFaktorNeu' | 'bisherigerUmsatzFaktor' | 'umsatzAuffangProzent'
                >
            >
    ) => void;
    updatePersonalAnpassung: (id: string, updates: Partial<Omit<PersonalAnpassung, 'id'>>) => void;
    removePersonalAnpassung: (id: string) => void;

    updateUnterschrift: (updates: Partial<Unterschrift>) => void;
    resetPeProfit: () => void;
    hydratePeProfitFromClientData: () => void;
    hydratePlanungsAnalyseFromClientData: () => void;
}

// +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
// SLICE CREATOR
// +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++

const getWorkspace = (state: AppState): PeProfitWorkspace =>
    state.peProfitWorkspace === 'planungsAnalyse' ? 'planungsAnalyse' : 'peProfit';

const resolvePeProfitPrognose = (state: AppState): PEProfitPrognose => {
    if (getWorkspace(state) === 'planungsAnalyse') {
        return state.planungsAnalysePrognose ?? createDefaultPEProfitPrognose();
    }
    return state.peProfitPrognose ?? createDefaultPEProfitPrognose();
};

const roundEuro = (value: number): number => Math.round(Number(forceNumber(value)));

const nextPeProfitVersion = (state: AppState): number => (state.peProfitDataVersion ?? 0) + 1;

const clonePrognoseArray = <T extends object>(items: T[] | undefined): T[] =>
    (items ?? []).map((item) => ({ ...item }));

const commitPeProfitPrognose = (
    state: AppState,
    peProfitPrognose: PEProfitPrognose
): {
    peProfitPrognose?: PEProfitPrognose;
    planungsAnalysePrognose?: PEProfitPrognose;
    peProfitDataVersion: number;
} => {
    const committed: PEProfitPrognose = {
        ...peProfitPrognose,
        gemeinkostenDetailAnpassungen: clonePrognoseArray(peProfitPrognose.gemeinkostenDetailAnpassungen),
        personalAnpassungen: clonePrognoseArray(peProfitPrognose.personalAnpassungen),
        neueDienstleistungen: clonePrognoseArray(peProfitPrognose.neueDienstleistungen),
        neueInvestitionen: clonePrognoseArray(peProfitPrognose.neueInvestitionen),
        unterschrift: { ...peProfitPrognose.unterschrift },
    };
    const peProfitDataVersion = nextPeProfitVersion(state);
    if (getWorkspace(state) === 'planungsAnalyse') {
        return { planungsAnalysePrognose: committed, peProfitDataVersion };
    }
    return { peProfitPrognose: committed, peProfitDataVersion };
};

const clonePnlBlock = (block: PEPnlBlock): PEPnlBlock => ({ ...block });

const resolvePlanungsArchiv = (state: AppState): PlanungsSzenario[] => {
    if (getWorkspace(state) === 'planungsAnalyse') {
        const memory = state.planungsAnalyseArchiv;
        if (Array.isArray(memory) && memory.length > 0) return memory;
        const fromClient = state.clientData?.planungsAnalyseArchiv;
        if (Array.isArray(fromClient) && fromClient.length > 0) return fromClient;
        return Array.isArray(memory) ? memory : [];
    }
    const memory = state.planungsArchiv;
    if (Array.isArray(memory) && memory.length > 0) return memory;
    const fromClient = state.clientData?.planungsArchiv;
    if (Array.isArray(fromClient) && fromClient.length > 0) return fromClient;
    return Array.isArray(memory) ? memory : [];
};

const resolveActiveSzenarioId = (state: AppState): string | null =>
    getWorkspace(state) === 'planungsAnalyse'
        ? state.planungsAnalyseActiveSzenarioId ?? null
        : state.activeSzenarioId ?? null;

const buildSollPnlSnapshot = (
    state: AppState,
    prognose: PEProfitPrognose,
    basisDatenTyp: PlanungsBasisDatenTyp = 'aktuell'
): PEPnlBlock => {
    const baseCalc = calculateAllMetrics(state.clientData, state.settings);
    const calc = resolveCalcForPlanungsBasis(
        baseCalc,
        basisDatenTyp,
        state.clientData,
        state.settings
    );
    return clonePnlBlock(calculatePeProfitMetrics(calc, prognose).prognose);
};

const normalizePlanungsSzenario = (stored: Partial<PlanungsSzenario>): PlanungsSzenario => ({
    id: stored.id ?? generateSimpleId(),
    timestamp: stored.timestamp ?? Date.now(),
    titel: stored.titel ?? 'Planungsanalyse',
    basisDatenTyp: stored.basisDatenTyp ?? 'aktuell',
    prognoseDaten: normalizeStoredPrognose(stored.prognoseDaten ?? {}),
    sollPnlSnapshot: clonePnlBlock(
        stored.sollPnlSnapshot ?? {
            umsatzNetto: 0,
            wareneinsatz: 0,
            rohertrag: 0,
            personalkostenProduktiv: 0,
            personalkostenUnproduktiv: 0,
            betriebskosten: 0,
            geraetekosten: 0,
            sonstigeEinnahmen: 0,
            investitionen: 0,
            gesamtkosten: 0,
            betriebsergebnis: 0,
            einkommensteuer: 0,
            inhaberertrag: 0,
            rentabilitaetProzent: 0,
            materialquoteProzent: 0,
        }
    ),
    ...(typeof stored.bankerPitchText === 'string' && stored.bankerPitchText.trim()
        ? { bankerPitchText: stored.bankerPitchText }
        : {}),
});

const persistPlanungsClientData = (
    get: () => AppState,
    peProfitPrognose: PEProfitPrognose,
    planungsArchiv: PlanungsSzenario[],
    activeSzenarioId: string | null
): void => {
    const ws = getWorkspace(get());
    if (ws === 'planungsAnalyse') {
        get().setClientData((prev) => ({
            ...prev,
            planungsAnalysePrognose: peProfitPrognose,
            planungsAnalyseArchiv: planungsArchiv,
            planungsAnalyseActiveSzenarioId: activeSzenarioId,
        }));
        return;
    }
    get().setClientData((prev) => ({
        ...prev,
        peProfitPrognose,
        planungsArchiv,
        activeSzenarioId,
    }));
};

const workspaceArchivPatch = (
    state: AppState,
    archiv: PlanungsSzenario[],
    activeId: string | null
) =>
    getWorkspace(state) === 'planungsAnalyse'
        ? {
              planungsAnalyseArchiv: archiv,
              planungsAnalyseActiveSzenarioId: activeId,
          }
        : {
              planungsArchiv: archiv,
              activeSzenarioId: activeId,
          };

const withPeProfitPersist = (
    get: () => AppState,
    state: AppState,
    peProfitPrognose: PEProfitPrognose
) => {
    const patch = commitPeProfitPrognose(state, peProfitPrognose);
    const activeId = resolveActiveSzenarioId(state);
    let archiv = resolvePlanungsArchiv(state).map((entry) => normalizePlanungsSzenario(entry));
    const committedPrognose =
        patch.planungsAnalysePrognose ?? patch.peProfitPrognose ?? peProfitPrognose;

    if (activeId) {
        const activeEntry = archiv.find((entry) => entry.id === activeId);
        const basisDatenTyp = activeEntry?.basisDatenTyp ?? 'aktuell';
        const sollPnl = buildSollPnlSnapshot(state, committedPrognose, basisDatenTyp);
        const exists = archiv.some((entry) => entry.id === activeId);
        if (exists) {
            archiv = archiv.map((entry) =>
                entry.id === activeId
                    ? {
                          ...entry,
                          titel:
                              committedPrognose.szenarioName?.trim() ||
                              entry.titel ||
                              'Planungsanalyse',
                          prognoseDaten: committedPrognose,
                          sollPnlSnapshot: sollPnl,
                          timestamp: Date.now(),
                      }
                    : entry
            );
        }
    }

    persistPlanungsClientData(get, committedPrognose, archiv, activeId);

    return {
        ...patch,
        ...workspaceArchivPatch(state, archiv, activeId),
    };
};

const normalizeStoredPrognose = (stored: Partial<PEProfitPrognose>): PEProfitPrognose => {
    const defaults = createDefaultPEProfitPrognose();
    return {
        ...defaults,
        ...stored,
        materialNotiz:
            typeof stored.materialNotiz === 'string' ? stored.materialNotiz : defaults.materialNotiz,
        unternehmerLohnZielMonat:
            stored.unternehmerLohnZielMonat === null || stored.unternehmerLohnZielMonat === undefined
                ? null
                : roundEuro(Number(stored.unternehmerLohnZielMonat)),
        unternehmerUmsatzDeltaMonat: roundEuro(
            Number(stored.unternehmerUmsatzDeltaMonat ?? defaults.unternehmerUmsatzDeltaMonat)
        ),
        unternehmerUmsatzAufGesamtAnrechnen: Boolean(
            stored.unternehmerUmsatzAufGesamtAnrechnen ?? defaults.unternehmerUmsatzAufGesamtAnrechnen
        ),
        unternehmerUmsatzAuffangProzent: Math.min(
            100,
            Math.max(
                0,
                Number(
                    stored.unternehmerUmsatzAuffangProzent ?? defaults.unternehmerUmsatzAuffangProzent
                )
            )
        ),
        gemeinkostenDetailAnpassungen: clonePrognoseArray(
            stored.gemeinkostenDetailAnpassungen ?? defaults.gemeinkostenDetailAnpassungen
        ),
        personalAnpassungen: clonePrognoseArray(stored.personalAnpassungen ?? defaults.personalAnpassungen),
        neueDienstleistungen: clonePrognoseArray(stored.neueDienstleistungen ?? defaults.neueDienstleistungen),
        neueInvestitionen: clonePrognoseArray(stored.neueInvestitionen ?? defaults.neueInvestitionen),
        unterschrift: { ...defaults.unterschrift, ...(stored.unterschrift ?? {}) },
    };
};

export const createPeProfitSlice: StateCreator<AppState, [], [], PeProfitSliceType> = (set, get) => ({
    peProfitPrognose: createDefaultPEProfitPrognose(),
    peProfitDataVersion: 0,
    planungsArchiv: [],
    activeSzenarioId: null,
    peProfitBasisDatenTyp: 'start',
    peProfitWorkspace: 'peProfit',
    planungsAnalysePrognose: createDefaultPEProfitPrognose(),
    planungsAnalyseArchiv: [],
    planungsAnalyseActiveSzenarioId: null,
    planungsAnalyseBasisDatenTyp: 'start',

    setPeProfitWorkspace: (workspace) => set({ peProfitWorkspace: workspace }),

    createPlanungsSzenario: (titel, basisDatenTyp) =>
        set((state) => {
            const id = generateSimpleId();
            const prognose: PEProfitPrognose = {
                ...createDefaultPEProfitPrognose(),
                szenarioName: titel.trim() || 'Planungsanalyse',
            };
            const sollPnlSnapshot = buildSollPnlSnapshot(state, prognose, basisDatenTyp);
            const entry: PlanungsSzenario = {
                id,
                timestamp: Date.now(),
                titel: titel.trim() || 'Planungsanalyse',
                basisDatenTyp,
                prognoseDaten: prognose,
                sollPnlSnapshot,
            };
            const archiv = [...resolvePlanungsArchiv(state), entry];
            const patch = commitPeProfitPrognose(state, prognose);
            const committedPrognose =
                patch.planungsAnalysePrognose ?? patch.peProfitPrognose ?? prognose;
            persistPlanungsClientData(get, committedPrognose, archiv, id);
            return {
                ...patch,
                ...workspaceArchivPatch(state, archiv, id),
            };
        }),

    loadPlanungsSzenario: (id) =>
        set((state) => {
            const archiv = resolvePlanungsArchiv(state);
            const entry = archiv.find((item) => item.id === id);
            if (!entry) return state;
            const normalized = normalizePlanungsSzenario(entry);
            const patch = commitPeProfitPrognose(state, normalized.prognoseDaten);
            const committedPrognose =
                patch.planungsAnalysePrognose ??
                patch.peProfitPrognose ??
                normalized.prognoseDaten;
            persistPlanungsClientData(get, committedPrognose, archiv, id);
            return {
                ...patch,
                ...workspaceArchivPatch(state, archiv, id),
            };
        }),

    setActiveSzenarioId: (id) =>
        set((state) => {
            persistPlanungsClientData(
                get,
                resolvePeProfitPrognose(state),
                resolvePlanungsArchiv(state),
                id
            );
            return workspaceArchivPatch(state, resolvePlanungsArchiv(state), id);
        }),

    updateActiveSzenarioBasis: (basis) =>
        set((state) => {
            const activeId = resolveActiveSzenarioId(state);
            if (!activeId) return state;

            const archiv = resolvePlanungsArchiv(state).map((entry) =>
                entry.id === activeId
                    ? { ...normalizePlanungsSzenario(entry), basisDatenTyp: basis }
                    : normalizePlanungsSzenario(entry)
            );

            persistPlanungsClientData(get, resolvePeProfitPrognose(state), archiv, activeId);

            return {
                ...workspaceArchivPatch(state, archiv, activeId),
                peProfitDataVersion: nextPeProfitVersion(state),
            };
        }),

    setPeProfitBasisDatenTyp: (basis) =>
        set((state) => {
            if (getWorkspace(state) === 'planungsAnalyse') {
                get().setClientData((prev) => ({
                    ...prev,
                    planungsAnalyseBasisDatenTyp: basis,
                }));
                return {
                    planungsAnalyseBasisDatenTyp: basis,
                    peProfitDataVersion: nextPeProfitVersion(state),
                };
            }
            get().setClientData((prev) => ({
                ...prev,
                peProfitBasisDatenTyp: basis,
            }));
            return {
                peProfitBasisDatenTyp: basis,
                peProfitDataVersion: nextPeProfitVersion(state),
            };
        }),

    renamePlanungsSzenario: (id, titel) =>
        set((state) => {
            const nextTitel = titel.trim() || 'Rentabilitätsplanung';
            const archiv = resolvePlanungsArchiv(state).map((entry) => {
                if (entry.id !== id) return normalizePlanungsSzenario(entry);
                const normalized = normalizePlanungsSzenario(entry);
                return {
                    ...normalized,
                    titel: nextTitel,
                    prognoseDaten: {
                        ...normalized.prognoseDaten,
                        szenarioName: nextTitel,
                    },
                };
            });

            const activeId = resolveActiveSzenarioId(state);
            const isActive = activeId === id;
            const basePrognose = resolvePeProfitPrognose(state);
            const peProfitPrognose = isActive
                ? {
                      ...basePrognose,
                      szenarioName: nextTitel,
                  }
                : basePrognose;

            const patch = isActive
                ? commitPeProfitPrognose(state, peProfitPrognose)
                : getWorkspace(state) === 'planungsAnalyse'
                  ? {
                        planungsAnalysePrognose: peProfitPrognose,
                        peProfitDataVersion: state.peProfitDataVersion,
                    }
                  : {
                        peProfitPrognose,
                        peProfitDataVersion: state.peProfitDataVersion,
                    };

            const committedPrognose =
                patch.planungsAnalysePrognose ?? patch.peProfitPrognose ?? peProfitPrognose;

            persistPlanungsClientData(get, committedPrognose, archiv, activeId);

            return {
                ...patch,
                ...workspaceArchivPatch(state, archiv, activeId),
            };
        }),

    setActiveSzenarioBankerPitch: (text) =>
        set((state) => {
            // Banker-Pitch gehört ausschließlich zu PE-Profit (nicht Planungsanalyse)
            const activeId = state.activeSzenarioId;
            if (!activeId) return state;

            const pitch = String(text ?? '').trim();
            if (!pitch) return state;

            const memory = state.planungsArchiv;
            const fromClient = state.clientData?.planungsArchiv;
            const sourceArchiv =
                Array.isArray(memory) && memory.length > 0
                    ? memory
                    : Array.isArray(fromClient) && fromClient.length > 0
                      ? fromClient
                      : Array.isArray(memory)
                        ? memory
                        : [];

            const archiv = sourceArchiv.map((entry) => {
                const normalized = normalizePlanungsSzenario(entry);
                if (normalized.id !== activeId) return normalized;
                return {
                    ...normalized,
                    bankerPitchText: pitch,
                    timestamp: Date.now(),
                };
            });

            if (!archiv.some((entry) => entry.id === activeId)) return state;

            const prognose = state.peProfitPrognose ?? createDefaultPEProfitPrognose();
            get().setClientData((prev) => ({
                ...prev,
                peProfitPrognose: prognose,
                planungsArchiv: archiv,
                activeSzenarioId: activeId,
            }));
            return {
                planungsArchiv: archiv,
            };
        }),

    clearActiveSzenarioBankerPitch: () =>
        set((state) => {
            const activeId = state.activeSzenarioId;
            if (!activeId) return state;

            const memory = state.planungsArchiv;
            const fromClient = state.clientData?.planungsArchiv;
            const sourceArchiv =
                Array.isArray(memory) && memory.length > 0
                    ? memory
                    : Array.isArray(fromClient) && fromClient.length > 0
                      ? fromClient
                      : Array.isArray(memory)
                        ? memory
                        : [];

            const archiv = sourceArchiv.map((entry) => {
                const normalized = normalizePlanungsSzenario(entry);
                if (normalized.id !== activeId) return normalized;
                const { bankerPitchText: _removed, ...rest } = normalized;
                return {
                    ...rest,
                    timestamp: Date.now(),
                };
            });

            if (!archiv.some((entry) => entry.id === activeId)) return state;

            const prognose = state.peProfitPrognose ?? createDefaultPEProfitPrognose();
            get().setClientData((prev) => ({
                ...prev,
                peProfitPrognose: prognose,
                planungsArchiv: archiv,
                activeSzenarioId: activeId,
            }));
            return {
                planungsArchiv: archiv,
            };
        }),

    deletePlanungsSzenario: (id) =>
        set((state) => {
            const archiv = resolvePlanungsArchiv(state).filter((entry) => entry.id !== id);
            const activeId = resolveActiveSzenarioId(state);
            const nextActiveId = activeId === id ? null : activeId;
            persistPlanungsClientData(get, resolvePeProfitPrognose(state), archiv, nextActiveId);
            return workspaceArchivPatch(state, archiv, nextActiveId);
        }),

    setPreisAnpassung: (prozent) =>
        set((state) => {
            const prognose = resolvePeProfitPrognose(state);
            return withPeProfitPersist(get, state, { ...prognose, preisAnpassungProzent: prozent });
        }),

    setKundenVerlustProzent: (prozent) =>
        set((state) => {
            const prognose = resolvePeProfitPrognose(state);
            return withPeProfitPersist(get, state, {
                ...prognose,
                kundenVerlustProzent: prozent === null ? null : Number(prozent),
            });
        }),

    setMaterialKostenAnpassung: (prozent) =>
        set((state) => {
            const prognose = resolvePeProfitPrognose(state);
            return withPeProfitPersist(get, state, { ...prognose, materialKostenAnpassungProzent: prozent });
        }),

    setMaterialZielMonat: (monat) =>
        set((state) => {
            const prognose = resolvePeProfitPrognose(state);
            const nextMonat = monat === null ? null : roundEuro(monat);
            return withPeProfitPersist(get, state, {
                ...prognose,
                materialZielMonat: nextMonat,
                // Keine Begründung ohne Kostenänderung
                ...(nextMonat === null ? { materialNotiz: '' } : {}),
            });
        }),

    setMaterialNotiz: (notiz) =>
        set((state) => {
            const prognose = resolvePeProfitPrognose(state);
            return withPeProfitPersist(get, state, {
                ...prognose,
                materialNotiz: String(notiz ?? ''),
            });
        }),

    setUnternehmerLohnZielMonat: (monat) =>
        set((state) => {
            const prognose = resolvePeProfitPrognose(state);
            return withPeProfitPersist(get, state, {
                ...prognose,
                unternehmerLohnZielMonat: monat === null ? null : roundEuro(monat),
            });
        }),

    setUnternehmerUmsatzDeltaMonat: (deltaMonat) =>
        set((state) => {
            const prognose = resolvePeProfitPrognose(state);
            return withPeProfitPersist(get, state, {
                ...prognose,
                unternehmerUmsatzDeltaMonat: roundEuro(deltaMonat),
            });
        }),

    setUnternehmerUmsatzAufGesamtAnrechnen: (aktiv) =>
        set((state) => {
            const prognose = resolvePeProfitPrognose(state);
            return withPeProfitPersist(get, state, {
                ...prognose,
                unternehmerUmsatzAufGesamtAnrechnen: Boolean(aktiv),
            });
        }),

    setUnternehmerUmsatzAuffangProzent: (prozent) =>
        set((state) => {
            const prognose = resolvePeProfitPrognose(state);
            const clamped = Math.min(100, Math.max(0, Number(prozent) || 0));
            return withPeProfitPersist(get, state, {
                ...prognose,
                unternehmerUmsatzAuffangProzent: clamped,
            });
        }),

    setAnsichtsModus: (modus) =>
        set((state) => {
            const prognose = resolvePeProfitPrognose(state);
            return withPeProfitPersist(get, state, { ...prognose, ansichtsModus: modus });
        }),

    setSzenarioName: (name) =>
        set((state) => {
            const prognose = resolvePeProfitPrognose(state);
            return withPeProfitPersist(get, state, { ...prognose, szenarioName: name });
        }),

    addNeueDienstleistung: (defaults) =>
        set((state) => {
            const prognose = resolvePeProfitPrognose(state);
            const neueZeile: Dienstleistungserweiterung = {
                id: generateSimpleId(),
                name: defaults?.name ?? '',
                jahresumsatzNetto: roundEuro(Number(defaults?.jahresumsatzNetto ?? 0)),
                startMonat: defaults?.startMonat ?? 1,
                aktiv: defaults?.aktiv ?? true,
            };
            return withPeProfitPersist(get, state, {
                ...prognose,
                neueDienstleistungen: [...(prognose.neueDienstleistungen ?? []), neueZeile],
            });
        }),

    updateNeueDienstleistung: (id, updates) =>
        set((state) => {
            const prognose = resolvePeProfitPrognose(state);
            return withPeProfitPersist(get, state, {
                ...prognose,
                neueDienstleistungen: (prognose.neueDienstleistungen ?? []).map((item) =>
                    item.id === id ? { ...item, ...updates } : item
                ),
            });
        }),

    removeNeueDienstleistung: (id) =>
        set((state) => {
            const prognose = resolvePeProfitPrognose(state);
            return withPeProfitPersist(get, state, {
                ...prognose,
                neueDienstleistungen: (prognose.neueDienstleistungen ?? []).filter((item) => item.id !== id),
            });
        }),

    addGemeinkostenDetailAnpassung: (defaults) =>
        set((state) => {
            const prognose = resolvePeProfitPrognose(state);
            const bestehend = [...(prognose.gemeinkostenDetailAnpassungen ?? [])];
            const neueZeile: GemeinkostenDetailAnpassung = {
                id: generateSimpleId(),
                bwaKey: defaults.bwaKey,
                label: defaults.label,
                istWert: roundEuro(defaults.istWert),
                neuerZielwert: roundEuro(defaults.neuerZielwert),
                notiz: defaults.notiz?.trim() || undefined,
            };
            return withPeProfitPersist(get, state, {
                ...prognose,
                gemeinkostenDetailAnpassungen: [...bestehend, neueZeile],
            });
        }),

    upsertGemeinkostenDetailAnpassung: (defaults) =>
        set((state) => {
            const prognose = resolvePeProfitPrognose(state);
            const liste = [...(prognose.gemeinkostenDetailAnpassungen ?? [])];
            const istWert = roundEuro(defaults.istWert);
            const neuerZielwert = roundEuro(defaults.neuerZielwert);
            const index = liste.findIndex((item) => item.bwaKey === defaults.bwaKey);
            const notiz =
                defaults.notiz !== undefined ? defaults.notiz.trim() || undefined : undefined;

            if (neuerZielwert === istWert) {
                const bestehendeNotiz = index >= 0 ? liste[index].notiz : undefined;
                const effektiveNotiz = notiz !== undefined ? notiz : bestehendeNotiz;
                if (!effektiveNotiz) {
                    if (index < 0) return state;
                    return withPeProfitPersist(get, state, {
                        ...prognose,
                        gemeinkostenDetailAnpassungen: liste.filter(
                            (item) => item.bwaKey !== defaults.bwaKey
                        ),
                    });
                }
                if (index >= 0) {
                    liste[index] = {
                        ...liste[index],
                        label: defaults.label,
                        istWert,
                        neuerZielwert,
                        notiz: effektiveNotiz,
                    };
                } else {
                    liste.push({
                        id: generateSimpleId(),
                        bwaKey: defaults.bwaKey,
                        label: defaults.label,
                        istWert,
                        neuerZielwert,
                        notiz: effektiveNotiz,
                    });
                }
                return withPeProfitPersist(get, state, {
                    ...prognose,
                    gemeinkostenDetailAnpassungen: liste,
                });
            }

            if (index >= 0) {
                liste[index] = {
                    ...liste[index],
                    label: defaults.label,
                    istWert,
                    neuerZielwert,
                    ...(notiz !== undefined ? { notiz } : {}),
                };
            } else {
                liste.push({
                    id: generateSimpleId(),
                    bwaKey: defaults.bwaKey,
                    label: defaults.label,
                    istWert,
                    neuerZielwert,
                    notiz,
                });
            }

            return withPeProfitPersist(get, state, {
                ...prognose,
                gemeinkostenDetailAnpassungen: liste,
            });
        }),

    updateGemeinkostenDetailAnpassung: (id, updates) =>
        set((state) => {
            const prognose = resolvePeProfitPrognose(state);
            return withPeProfitPersist(get, state, {
                ...prognose,
                gemeinkostenDetailAnpassungen: (prognose.gemeinkostenDetailAnpassungen ?? []).map((item) =>
                    item.id === id ? { ...item, ...updates } : item
                ),
            });
        }),

    removeGemeinkostenDetailAnpassung: (id) =>
        set((state) => {
            const prognose = resolvePeProfitPrognose(state);
            return withPeProfitPersist(get, state, {
                ...prognose,
                gemeinkostenDetailAnpassungen: (prognose.gemeinkostenDetailAnpassungen ?? []).filter(
                    (item) => item.id !== id
                ),
            });
        }),

    addNeueInvestition: (defaults) =>
        set((state) => {
            const prognose = resolvePeProfitPrognose(state);
            const neueZeile: Investition = {
                id: generateSimpleId(),
                name: defaults?.name ?? '',
                monatskosten: roundEuro(defaults?.monatskosten ?? 0),
                laufzeitMonate: defaults?.laufzeitMonate ?? 12,
                aktiv: defaults?.aktiv ?? true,
            };
            return withPeProfitPersist(get, state, {
                ...prognose,
                neueInvestitionen: [...(prognose.neueInvestitionen ?? []), neueZeile],
            });
        }),

    updateNeueInvestition: (id, updates) =>
        set((state) => {
            const prognose = resolvePeProfitPrognose(state);
            return withPeProfitPersist(get, state, {
                ...prognose,
                neueInvestitionen: (prognose.neueInvestitionen ?? []).map((item) =>
                    item.id === id ? { ...item, ...updates } : item
                ),
            });
        }),

    removeNeueInvestition: (id) =>
        set((state) => {
            const prognose = resolvePeProfitPrognose(state);
            return withPeProfitPersist(get, state, {
                ...prognose,
                neueInvestitionen: (prognose.neueInvestitionen ?? []).filter((item) => item.id !== id),
            });
        }),

    addPersonalAnpassung: (defaults) =>
        set((state) => {
            const prognose = resolvePeProfitPrognose(state);
            const bestehend = [...(prognose.personalAnpassungen ?? [])];
            const isNeu = defaults?.mitarbeiterId === PE_PROFIT_NEUER_MITARBEITER_ID;
            const neueZeile: PersonalAnpassung = {
                id: generateSimpleId(),
                mitarbeiterId: defaults?.mitarbeiterId ?? '',
                mitarbeiterName: defaults?.mitarbeiterName ?? (isNeu ? 'Neuer Mitarbeiter' : ''),
                istWert: roundEuro(defaults?.istWert ?? 0),
                neuerZielwert: roundEuro(defaults?.neuerZielwert ?? 0),
                aktiv: defaults?.aktiv ?? true,
                umsatzFaktorNeu: isNeu
                    ? Number(defaults?.umsatzFaktorNeu ?? PE_PROFIT_DEFAULT_UMSATZ_FAKTOR_NEU)
                    : undefined,
                entlassen: defaults?.entlassen ?? false,
                bisherigerUmsatzFaktor: defaults?.bisherigerUmsatzFaktor ?? PE_PROFIT_DEFAULT_UMSATZ_FAKTOR_BISHER,
                umsatzAuffangProzent:
                    defaults?.umsatzAuffangProzent ?? PE_PROFIT_DEFAULT_UMSATZ_AUFFANG_PROZENT,
            };
            return withPeProfitPersist(get, state, {
                ...prognose,
                personalAnpassungen: [...bestehend, neueZeile],
            });
        }),

    upsertPersonalAnpassung: (defaults) =>
        set((state) => {
            const prognose = resolvePeProfitPrognose(state);
            const liste = [...(prognose.personalAnpassungen ?? [])];
            const istWert = roundEuro(defaults.istWert);
            const neuerZielwert = roundEuro(defaults.neuerZielwert);
            const entlassen = defaults.entlassen ?? false;
            const index = liste.findIndex((item) => item.mitarbeiterId === defaults.mitarbeiterId);

            if (neuerZielwert === istWert && !entlassen) {
                if (index < 0) return state;
                return withPeProfitPersist(get, state, {
                    ...prognose,
                    personalAnpassungen: liste.filter((item) => item.mitarbeiterId !== defaults.mitarbeiterId),
                });
            }

            if (index >= 0) {
                liste[index] = {
                    ...liste[index],
                    mitarbeiterName: defaults.mitarbeiterName,
                    istWert,
                    neuerZielwert: entlassen ? 0 : neuerZielwert,
                    aktiv: true,
                    ...(defaults.entlassen !== undefined ? { entlassen } : {}),
                    ...(defaults.umsatzFaktorNeu !== undefined
                        ? { umsatzFaktorNeu: defaults.umsatzFaktorNeu }
                        : {}),
                    ...(defaults.bisherigerUmsatzFaktor !== undefined
                        ? { bisherigerUmsatzFaktor: defaults.bisherigerUmsatzFaktor }
                        : {}),
                    ...(defaults.umsatzAuffangProzent !== undefined
                        ? { umsatzAuffangProzent: defaults.umsatzAuffangProzent }
                        : {}),
                };
            } else {
                liste.push({
                    id: generateSimpleId(),
                    mitarbeiterId: defaults.mitarbeiterId,
                    mitarbeiterName: defaults.mitarbeiterName,
                    istWert,
                    neuerZielwert: entlassen ? 0 : neuerZielwert,
                    aktiv: true,
                    umsatzFaktorNeu:
                        defaults.mitarbeiterId === PE_PROFIT_NEUER_MITARBEITER_ID
                            ? Number(defaults.umsatzFaktorNeu ?? PE_PROFIT_DEFAULT_UMSATZ_FAKTOR_NEU)
                            : undefined,
                    entlassen,
                    bisherigerUmsatzFaktor:
                        defaults.bisherigerUmsatzFaktor ?? PE_PROFIT_DEFAULT_UMSATZ_FAKTOR_BISHER,
                    umsatzAuffangProzent:
                        defaults.umsatzAuffangProzent ?? PE_PROFIT_DEFAULT_UMSATZ_AUFFANG_PROZENT,
                });
            }

            return withPeProfitPersist(get, state, {
                ...prognose,
                personalAnpassungen: liste,
            });
        }),

    updatePersonalAnpassung: (id, updates) =>
        set((state) => {
            const prognose = resolvePeProfitPrognose(state);
            return withPeProfitPersist(get, state, {
                ...prognose,
                personalAnpassungen: (prognose.personalAnpassungen ?? []).map((item) =>
                    item.id === id ? { ...item, ...updates } : item
                ),
            });
        }),

    removePersonalAnpassung: (id) =>
        set((state) => {
            const prognose = resolvePeProfitPrognose(state);
            return withPeProfitPersist(get, state, {
                ...prognose,
                personalAnpassungen: (prognose.personalAnpassungen ?? []).filter((item) => item.id !== id),
            });
        }),

    updateUnterschrift: (updates) =>
        set((state) => {
            const prognose = resolvePeProfitPrognose(state);
            return withPeProfitPersist(get, state, {
                ...prognose,
                unterschrift: {
                    ...prognose.unterschrift,
                    ...updates,
                },
            });
        }),

    resetPeProfit: () => {
        const fresh = createDefaultPEProfitPrognose();
        const state = get();
        const memory = state.planungsArchiv;
        const fromClient = state.clientData?.planungsArchiv;
        const archiv =
            Array.isArray(memory) && memory.length > 0
                ? memory
                : Array.isArray(fromClient) && fromClient.length > 0
                  ? fromClient
                  : Array.isArray(memory)
                    ? memory
                    : [];
        get().setClientData((prev) => ({
            ...prev,
            peProfitPrognose: fresh,
            planungsArchiv: archiv,
            activeSzenarioId: state.activeSzenarioId,
        }));
        set({
            peProfitPrognose: fresh,
            peProfitDataVersion: 0,
        });
    },

    hydratePeProfitFromClientData: () =>
        set((state) => {
            const cd = state.clientData ?? {};
            const archiv = Array.isArray(cd.planungsArchiv)
                ? cd.planungsArchiv.map((entry: Partial<PlanungsSzenario>) =>
                      normalizePlanungsSzenario(entry)
                  )
                : [];
            const activeId =
                typeof cd.activeSzenarioId === 'string' ? cd.activeSzenarioId : null;
            const peProfitBasisDatenTyp: PlanungsBasisDatenTyp =
                cd.peProfitBasisDatenTyp === 'aktuell' ? 'aktuell' : 'start';

            if (activeId) {
                const entry = archiv.find((item) => item.id === activeId);
                if (entry) {
                    return {
                        peProfitPrognose: {
                            ...entry.prognoseDaten,
                            gemeinkostenDetailAnpassungen: clonePrognoseArray(
                                entry.prognoseDaten.gemeinkostenDetailAnpassungen
                            ),
                            personalAnpassungen: clonePrognoseArray(
                                entry.prognoseDaten.personalAnpassungen
                            ),
                            neueDienstleistungen: clonePrognoseArray(
                                entry.prognoseDaten.neueDienstleistungen
                            ),
                            neueInvestitionen: clonePrognoseArray(
                                entry.prognoseDaten.neueInvestitionen
                            ),
                            unterschrift: { ...entry.prognoseDaten.unterschrift },
                        },
                        peProfitDataVersion: nextPeProfitVersion(state),
                        planungsArchiv: archiv,
                        activeSzenarioId: activeId,
                        peProfitBasisDatenTyp,
                    };
                }
            }

            const stored = cd.peProfitPrognose;
            if (stored && typeof stored === 'object') {
                return {
                    peProfitPrognose: normalizeStoredPrognose(stored),
                    peProfitDataVersion: nextPeProfitVersion(state),
                    planungsArchiv: archiv,
                    activeSzenarioId: null,
                    peProfitBasisDatenTyp,
                };
            }

            return {
                planungsArchiv: archiv,
                activeSzenarioId: null,
                peProfitBasisDatenTyp,
            };
        }),

    hydratePlanungsAnalyseFromClientData: () =>
        set((state) => {
            const cd = state.clientData ?? {};
            // Migration: planungsAnalyseArchiv startet leer — kein Blind-Copy von planungsArchiv
            const archiv = Array.isArray(cd.planungsAnalyseArchiv)
                ? cd.planungsAnalyseArchiv.map((entry: Partial<PlanungsSzenario>) =>
                      normalizePlanungsSzenario(entry)
                  )
                : [];
            const activeId =
                typeof cd.planungsAnalyseActiveSzenarioId === 'string'
                    ? cd.planungsAnalyseActiveSzenarioId
                    : null;
            const planungsAnalyseBasisDatenTyp: PlanungsBasisDatenTyp =
                cd.planungsAnalyseBasisDatenTyp === 'aktuell' ? 'aktuell' : 'start';

            if (activeId) {
                const entry = archiv.find((item) => item.id === activeId);
                if (entry) {
                    return {
                        planungsAnalysePrognose: {
                            ...entry.prognoseDaten,
                            gemeinkostenDetailAnpassungen: clonePrognoseArray(
                                entry.prognoseDaten.gemeinkostenDetailAnpassungen
                            ),
                            personalAnpassungen: clonePrognoseArray(
                                entry.prognoseDaten.personalAnpassungen
                            ),
                            neueDienstleistungen: clonePrognoseArray(
                                entry.prognoseDaten.neueDienstleistungen
                            ),
                            neueInvestitionen: clonePrognoseArray(
                                entry.prognoseDaten.neueInvestitionen
                            ),
                            unterschrift: { ...entry.prognoseDaten.unterschrift },
                        },
                        peProfitDataVersion: nextPeProfitVersion(state),
                        planungsAnalyseArchiv: archiv,
                        planungsAnalyseActiveSzenarioId: activeId,
                        planungsAnalyseBasisDatenTyp,
                    };
                }
            }

            const stored = cd.planungsAnalysePrognose;
            if (stored && typeof stored === 'object') {
                return {
                    planungsAnalysePrognose: normalizeStoredPrognose(stored),
                    peProfitDataVersion: nextPeProfitVersion(state),
                    planungsAnalyseArchiv: archiv,
                    planungsAnalyseActiveSzenarioId: null,
                    planungsAnalyseBasisDatenTyp,
                };
            }

            return {
                planungsAnalyseArchiv: archiv,
                planungsAnalyseActiveSzenarioId: null,
                planungsAnalyseBasisDatenTyp,
            };
        }),
});
